import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog, FormControl, FormControlLabel, Stack } from '@mui/material';
import ModalTemplate from '../../modal/modal-template';
import { BaseModalRef } from '@/app/helpers/types';
import { useOpen } from '@/app/hooks';
import { UIFormLabel } from '../../ui/ui-form-label';
import { FormNumericInput } from '../../inputs/form-numeric-input';
import UISwitch from '../../ui/ui-switch';
import {
  useCreateFactorsConditionMutation,
  useGetFactorsConditionInfoQuery,
  useUpdateFactorsConditionMutation,
} from '@/app/api';
import { getFetchEror } from '@/app/helpers';
import { useNotification } from '@/shared/contexts/NotificationContext';

const DEFAULT_FORM_VALUES: FormValues = {
  termInDays: 0,
  percentage: 0,
  isForDisclosedFactoring: false,
  isForUndisclosedFactoring: false,
  isForReverseFactoring: false,
  isForExportFactoring: false,
  isForImportFactoring: false,
};

interface Props {
  onSuccess?: () => void;
  editConditionId?: number | null;
}

interface FormValues {
  termInDays: number;
  percentage: number;
  isForDisclosedFactoring: boolean;
  isForUndisclosedFactoring: boolean;
  isForReverseFactoring: boolean;
  isForExportFactoring: boolean;
  isForImportFactoring: boolean;
}

export const FactoringConditionsModal = forwardRef<BaseModalRef, Props>(
  ({ onSuccess, editConditionId }, ref) => {
    const { t } = useTranslation();
    const { open, setOpen } = useOpen();
    const { showNotification } = useNotification();
    const [createFactorsCondition] = useCreateFactorsConditionMutation();
    const [updateFactorsCondition] = useUpdateFactorsConditionMutation();

    const { data: conditionData, isLoading: isLoadingCondition } =
      useGetFactorsConditionInfoQuery(
        { id: editConditionId! },
        {
          skip: !editConditionId,
          refetchOnMountOrArgChange: true,
        }
      );

    const isEditMode = !!editConditionId;

    const defaultValues: FormValues = DEFAULT_FORM_VALUES;

    const { reset, control, handleSubmit } = useForm<FormValues>({
      defaultValues,
    });

    const formData = useMemo(() => {
      if (!conditionData?.data) return DEFAULT_FORM_VALUES;

      return {
        termInDays: conditionData.data.termInDays || 0,
        percentage: conditionData.data.percentage || 0,
        isForDisclosedFactoring:
          conditionData.data.isForDisclosedFactoring || false,
        isForUndisclosedFactoring:
          conditionData.data.isForUndisclosedFactoring || false,
        isForReverseFactoring:
          conditionData.data.isForReverseFactoring || false,
        isForExportFactoring: conditionData.data.isForExportFactoring || false,
        isForImportFactoring: conditionData.data.isForImportFactoring || false,
      };
    }, [conditionData?.data]);

    useEffect(() => {
      if (isEditMode && open && conditionData?.data) {
        reset(formData);
      }
    }, [isEditMode, open, conditionData?.data, formData, reset]);

    useImperativeHandle(
      ref,
      () => ({
        open: onOpen,
        close: onClose,
      }),
      []
    );

    const onOpen = () => {
      setOpen(true);
    };

    const onClose = () => {
      setOpen(false);
      setTimeout(() => {
        reset(defaultValues);
      }, 300);
    };

    const onSubmit = useCallback(
      async (values: FormValues) => {
        try {
          if (isEditMode && editConditionId) {
            await updateFactorsCondition({
              id: editConditionId,
              body: values,
            }).unwrap();
            showNotification(
              t('settings.condition_successfully_updated'),
              'success'
            );
          } else {
            await createFactorsCondition({
              ...values,
              termInDays: +values.termInDays,
              percentage: +values.percentage,
            }).unwrap();
            showNotification(
              t('settings.condition_successfully_added'),
              'success'
            );
          }
          onSuccess?.();
        } catch (error) {
          showNotification(getFetchEror(error), 'error');
        } finally {
          onClose();
        }
      },
      [
        isEditMode,
        editConditionId,
        updateFactorsCondition,
        createFactorsCondition,
        showNotification,
        t,
        onSuccess,
        onClose,
      ]
    );

    const title = useMemo(
      () =>
        isEditMode
          ? t('settings.edit_condition_title')
          : t('settings.conditions_title'),
      [isEditMode, t]
    );

    const submitButtonText = useMemo(
      () => (isEditMode ? t('settings.save') : t('settings.add_condition')),
      [isEditMode, t]
    );

    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            maxWidth: 600,
            width: '100%',
          },
        }}
      >
        <ModalTemplate
          onClose={onClose}
          title={title}
          submitBtnText={submitButtonText}
          cancelBtnText={t('settings.cancel')}
          onSubmit={handleSubmit(onSubmit)}
          loading={isEditMode && isLoadingCondition}
        >
          <Stack spacing={3}>
            <FormControl required>
              <UIFormLabel>{t('settings.term_in_days')}:</UIFormLabel>
              <FormNumericInput
                control={control}
                name='termInDays'
                required
                rules={{
                  required: true,
                }}
              />
            </FormControl>

            <FormControl required>
              <UIFormLabel>{t('settings.percentage')}:</UIFormLabel>
              <FormNumericInput
                control={control}
                name='percentage'
                required
                decimalScale={2}
                rules={{
                  required: true,
                }}
              />
            </FormControl>

            <Stack spacing={2}>
              <Controller
                control={control}
                name='isForDisclosedFactoring'
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <UISwitch
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                    }
                    label={t('settings.for_disclosed_factoring')}
                    sx={{ mb: 1 }}
                    slotProps={{
                      typography: {
                        sx: {
                          fontSize: 14,
                          fontWeight: 600,
                          marginLeft: 1.2,
                        },
                      },
                    }}
                  />
                )}
              />

              <Controller
                control={control}
                name='isForUndisclosedFactoring'
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <UISwitch
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                    }
                    label={t('settings.for_undisclosed_factoring')}
                    sx={{ mb: 1 }}
                    slotProps={{
                      typography: {
                        sx: {
                          fontSize: 14,
                          fontWeight: 600,
                          marginLeft: 1.2,
                        },
                      },
                    }}
                  />
                )}
              />

              <Controller
                control={control}
                name='isForReverseFactoring'
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <UISwitch
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                    }
                    label={t('settings.for_reverse_factoring')}
                    sx={{ mb: 1 }}
                    slotProps={{
                      typography: {
                        sx: {
                          fontSize: 14,
                          fontWeight: 600,
                          marginLeft: 1.2,
                        },
                      },
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name='isForExportFactoring'
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <UISwitch
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                    }
                    label={t('settings.for_export_factoring')}
                    sx={{ mb: 1 }}
                    slotProps={{
                      typography: {
                        sx: {
                          fontSize: 14,
                          fontWeight: 600,
                          marginLeft: 1.2,
                        },
                      },
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name='isForImportFactoring'
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <UISwitch
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                    }
                    label={t('settings.for_import_factoring')}
                    sx={{ mb: 1 }}
                    slotProps={{
                      typography: {
                        sx: {
                          fontSize: 14,
                          fontWeight: 600,
                          marginLeft: 1.2,
                        },
                      },
                    }}
                  />
                )}
              />
            </Stack>
          </Stack>
        </ModalTemplate>
      </Dialog>
    );
  }
);
